import { Head, Link, usePage } from "@inertiajs/react"

import { AppShell } from "@/components/app-shell"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

function formatDate(value) {
  if (!value) return "-"

  return new Date(value).toLocaleString()
}

function statusClassName(status) {
  if (status === "completed") return "bg-[rgb(42_157_143/0.12)] text-[var(--color-verdigris)]"
  if (status === "failed") return "bg-[rgb(197_88_88/0.12)] text-[var(--color-danger)]"
  if (status === "running" || status === "queued") return "bg-[rgb(233_196_106/0.18)] text-[rgb(126_88_12)]"

  return "bg-[var(--color-panel)] text-[var(--color-text-muted)]"
}

function scoreLabel(value) {
  return value === null || value === undefined ? "-" : Number(value).toFixed(2)
}

function passLabel(value) {
  if (value === true) return "Passed"
  if (value === false) return "Failed"

  return "Pending"
}

function passClassName(value) {
  if (value === true) return "bg-[rgb(42_157_143/0.12)] text-[var(--color-verdigris)]"
  if (value === false) return "bg-[rgb(197_88_88/0.12)] text-[var(--color-danger)]"

  return "bg-[var(--color-panel)] text-[var(--color-text-muted)]"
}

function CitationList({ citations = [] }) {
  if (!Array.isArray(citations) || citations.length === 0) return null

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {citations.map((citation, index) => {
        const label = [
          citation?.title,
          citation?.page ? `page ${citation.page}` : null
        ].filter(Boolean).join(", ")

        if (citation?.url) {
          return (
            <a
              className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-charcoal-blue)] hover:text-[var(--color-verdigris)]"
              href={citation.url}
              key={`${citation.url}-${index}`}
            >
              {label || "Open citation"}
            </a>
          )
        }

        return (
          <span
            className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]"
            key={`${label}-${index}`}
          >
            {label || "Citation"}
          </span>
        )
      })}
    </div>
  )
}

function ResultRow({ result, index }) {
  const source = [
    result.source_document_title,
    result.source_page_number ? `page ${result.source_page_number}` : null
  ].filter(Boolean).join(", ")

  return (
    <article className="rounded-lg border border-[var(--color-border)] bg-white p-4 shadow-[var(--shadow-soft)]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">
            Case {index + 1}
          </p>
          <h3 className="mt-2 text-base font-semibold leading-6 text-[var(--color-charcoal-blue)]">
            {result.question}
          </h3>
          {source ? <p className="mt-2 text-xs text-[var(--color-text-muted)]">{source}</p> : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <span className={cn("rounded-full px-3 py-1 text-xs font-semibold uppercase", passClassName(result.passed))}>
            {passLabel(result.passed)}
          </span>
          <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
            Score {scoreLabel(result.score)}
          </span>
          <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
            {result.latency_ms ?? "-"} ms
          </span>
        </div>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Expected answer</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--color-text-muted)]">
            {result.canonical_expected_answer || result.expected_answer || "-"}
          </p>
          <CitationList citations={result.expected_citations} />
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Model answer</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--color-text-muted)]">
            {result.answer || "-"}
          </p>
          <CitationList citations={result.citations} />
        </div>
      </div>

      {result.judge_rationale ? (
        <div className="mt-4 rounded-lg bg-[var(--color-panel)] p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Judge rationale</p>
          <p className="mt-2 text-sm leading-6 text-[var(--color-text-muted)]">{result.judge_rationale}</p>
        </div>
      ) : null}

      {result.metadata?.answer_fallback_reason ? (
        <p className="mt-3 text-xs text-[var(--color-danger)]">
          Answer fallback: {result.metadata.answer_fallback_reason}
        </p>
      ) : null}
    </article>
  )
}

export default function EvalRunPage({ run }) {
  const { organization } = usePage().props
  const organizationBasePath = organization?.slug ? `/orgs/${organization.slug}` : ""
  const results = run?.results || []
  const datasetHref = run?.dataset?.url || (run?.dataset?.id ? `${organizationBasePath}/settings/benchmarks?dataset_id=${run.dataset.id}` : `${organizationBasePath}/settings/benchmarks`)

  return (
    <>
      <Head title={run ? `${run.methodology.label} evaluation` : "Evaluation run"} />

      <AppShell
        title={run ? `${run.methodology.label} evaluation` : "Evaluation run"}
        subtitle={run ? `${run.dataset.name} through ${run.methodology.retrieval_engine}` : "Inspect evaluation run results."}
        wide
        actions={(
          <Button asChild type="button" variant="outline">
            <Link href={datasetHref}>Back to dataset</Link>
          </Button>
        )}
      >
        {run ? (
          <div className="grid gap-6">
            <section className="rounded-[1.75rem] bg-white/82 p-5 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("rounded-full px-3 py-1 text-xs font-semibold uppercase", statusClassName(run.status))}>
                      {run.status}
                    </span>
                    <Link
                      className="text-sm font-medium text-[var(--color-verdigris)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4"
                      href={datasetHref}
                    >
                      {run.dataset.name}
                    </Link>
                  </div>
                  <p className="mt-3 text-sm text-[var(--color-text-muted)]">
                    Methodology key: {run.methodology.key}
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    Answer model: {run.methodology.answer_model}
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    Judge model: {run.judge_model || "-"}
                  </p>
                  {run.failure_reason ? <p className="mt-2 text-sm text-[var(--color-danger)]">{run.failure_reason}</p> : null}
                </div>

                <div className="grid grid-cols-2 gap-2 text-right sm:grid-cols-4">
                  <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                    <p className="text-xs text-[var(--color-text-faint)]">Avg score</p>
                    <p className="text-lg font-semibold text-[var(--color-charcoal-blue)]">{scoreLabel(run.average_score)}</p>
                  </div>
                  <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                    <p className="text-xs text-[var(--color-text-faint)]">Passed</p>
                    <p className="text-lg font-semibold text-[var(--color-charcoal-blue)]">{run.passed_cases ?? "-"} / {run.total_cases ?? results.length}</p>
                  </div>
                  <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                    <p className="text-xs text-[var(--color-text-faint)]">Created</p>
                    <p className="text-xs font-medium text-[var(--color-charcoal-blue)]">{formatDate(run.created_at)}</p>
                  </div>
                  <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                    <p className="text-xs text-[var(--color-text-faint)]">Finished</p>
                    <p className="text-xs font-medium text-[var(--color-charcoal-blue)]">{formatDate(run.finished_at)}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              {results.length > 0 ? results.map((result, index) => (
                <ResultRow index={index} key={result.eval_case_id} result={result} />
              )) : (
                <p className="rounded-lg border border-dashed border-[var(--color-border)] bg-white/82 p-5 text-sm text-[var(--color-text-muted)]">
                  This run has no evaluation cases to display.
                </p>
              )}
            </section>
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-[var(--color-border)] bg-white/82 p-5 text-sm text-[var(--color-text-muted)]">
            Evaluation run was not found.
          </p>
        )}
      </AppShell>
    </>
  )
}
